'use client';

import { useEffect } from 'react';
import { trackEvent } from '@/lib/analytics';

function getElementLabel(element: HTMLElement) {
  const ariaLabel = element.getAttribute('aria-label')?.trim();
  if (ariaLabel) return ariaLabel;

  const text = element.textContent?.replace(/\s+/g, ' ').trim();
  if (text) return text.slice(0, 120);

  if (element instanceof HTMLInputElement) {
    return element.value || element.name || 'input';
  }

  return element.tagName.toLowerCase();
}

function isTrackableElement(element: HTMLElement) {
  if (element instanceof HTMLButtonElement) return true;
  if (element instanceof HTMLInputElement) {
    return element.type === 'submit' || element.type === 'button';
  }
  if (element instanceof HTMLAnchorElement) {
    return (
      element.className.includes('btn-') ||
      element.className.includes('button') ||
      element.getAttribute('role') === 'button'
    );
  }
  return false;
}

export default function GlobalClickTracker() {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const clickable = target.closest('button, a, input[type="submit"], input[type="button"]');
      if (!(clickable instanceof HTMLElement) || !isTrackableElement(clickable)) return;

      const href = clickable instanceof HTMLAnchorElement ? clickable.href : undefined;

      trackEvent('ui_click', {
        label: getElementLabel(clickable),
        tag: clickable.tagName.toLowerCase(),
        id: clickable.id || undefined,
        classes:
          typeof clickable.className === 'string'
            ? clickable.className.split(/\s+/).filter(Boolean).slice(0, 6).join(' ')
            : undefined,
        href,
        path: window.location.pathname,
      });
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return null;
}
